import { useEffect, useMemo, useState } from "react";
import { Bell, LoaderCircle, Mail, MessageSquare, Save } from "lucide-react";
import { Button } from "../../../components/ui/button";
import { InlineMessage } from "../../../components/konto-helpers";
import { usePlatformAuth } from "../../../lib/use-platform-auth";

type NotificationPreferenceRow = {
  eventId: string;
  name: string;
  description: string;
  category: string;
  channels: {
    email: {
      available: boolean;
      enabled: boolean;
    };
    sms: {
      available: boolean;
      enabled: boolean;
    };
    inApp: {
      available: boolean;
      enabled: boolean;
    };
  };
};

function ChannelSwitch({
  checked,
  disabled,
  onChange,
  label,
}: {
  checked: boolean;
  disabled: boolean;
  onChange: () => void;
  label: string;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={onChange}
      className={`inline-flex h-7 w-12 items-center rounded-full p-1 transition-colors ${
        checked ? "bg-accent" : "bg-muted"
      } ${disabled ? "cursor-not-allowed opacity-50" : "cursor-pointer"}`}
    >
      <span
        className={`h-5 w-5 rounded-full bg-white shadow-sm transition-transform ${
          checked ? "translate-x-5" : "translate-x-0"
        }`}
      />
    </button>
  );
}

function clonePreferences(preferences: NotificationPreferenceRow[]) {
  return preferences.map((preference) => ({
    ...preference,
    channels: {
      email: { ...preference.channels.email },
      sms: { ...preference.channels.sms },
      inApp: { ...preference.channels.inApp },
    },
  }));
}

export function VarslerTab() {
  const { profile } = usePlatformAuth();
  const [preferences, setPreferences] = useState<NotificationPreferenceRow[]>([]);
  const [savedPreferences, setSavedPreferences] = useState<NotificationPreferenceRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadPreferences = async () => {
      setLoading(true);
      setError(null);

      try {
        const api = import.meta.env.VITE_API_URL || "http://localhost:9000";
        const response = await fetch(`${api}/api/users/notification-preferences`, {
          credentials: "include",
        });

        if (!response.ok) {
          throw new Error();
        }

        const data = (await response.json()) as NotificationPreferenceRow[];
        setPreferences(clonePreferences(data));
        setSavedPreferences(clonePreferences(data));
      } catch {
        setError("Vi klarte ikke å laste varselinnstillingene dine.");
      } finally {
        setLoading(false);
      }
    };

    void loadPreferences();
  }, []);

  const hasUnsavedChanges = useMemo(
    () => JSON.stringify(preferences) !== JSON.stringify(savedPreferences),
    [preferences, savedPreferences],
  );

  const handleToggle = (eventId: string, channel: "email" | "sms") => {
    setMessage(null);
    setError(null);
    setPreferences((current) => {
      return current.map((preference) => {
        if (preference.eventId !== eventId) {
          return preference;
        }

        return {
          ...preference,
          channels: {
            ...preference.channels,
            [channel]: {
              ...preference.channels[channel],
              enabled: !preference.channels[channel].enabled,
            },
          },
        };
      });
    });
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage(null);
    setError(null);

    try {
      const api = import.meta.env.VITE_API_URL || "http://localhost:9000";
      const response = await fetch(`${api}/api/users/notification-preferences`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          preferences: preferences.map((preference) => ({
            eventId: preference.eventId,
            email: preference.channels.email.enabled,
            sms: preference.channels.sms.enabled,
            inApp: false,
          })),
        }),
      });

      if (!response.ok) {
        throw new Error();
      }

      const data = (await response.json()) as NotificationPreferenceRow[];
      setPreferences(clonePreferences(data));
      setSavedPreferences(clonePreferences(data));
      setMessage("Varselinnstillingene dine er lagret.");
    } catch {
      setError("Vi klarte ikke å lagre varselinnstillingene dine.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <section
      className="rounded-3xl bg-white/50 p-8 backdrop-blur-sm animate-in fade-in slide-in-from-bottom-4 duration-500"
      style={{ border: "1px dashed var(--border)" }}
    >
      <div className="mb-10 flex flex-col gap-6 lg:flex-row lg:items-start lg:justify-between">
        <div className="space-y-3">
          <h2
            className="text-2xl font-black text-[#1a2e35]"
            style={{ fontFamily: "var(--font-display)" }}
          >
            Varsler
          </h2>
          <p className="max-w-2xl text-base font-medium leading-relaxed text-muted-foreground">
            Velg hvilke portalvarsler du vil motta på e-post og SMS. Sikkerhetsmeldinger og andre
            obligatoriske transaksjoner styres ikke her.
          </p>
        </div>

        <div className="rounded-2xl bg-white px-5 py-4 shadow-sm ring-1 ring-border">
          <p className="text-[10px] font-black uppercase tracking-[0.2em] text-muted-foreground/60">
            Kontaktkanaler
          </p>
          <div className="mt-3 space-y-2 text-sm font-semibold text-[#1a2e35]">
            <div className="flex items-center gap-2">
              <Mail className="h-4 w-4 text-accent" />
              <span>{profile?.email ?? "Ingen e-post registrert"}</span>
            </div>
            <div className="flex items-center gap-2">
              <MessageSquare className="h-4 w-4 text-accent" />
              <span>{profile?.phoneNumber ?? "Ingen mobil registrert"}</span>
            </div>
          </div>
        </div>
      </div>

      {error ? <InlineMessage tone="error">{error}</InlineMessage> : null}
      {message ? <InlineMessage tone="success">{message}</InlineMessage> : null}

      <div className="mt-6 overflow-hidden rounded-3xl bg-white shadow-sm ring-1 ring-border">
        <div className="grid grid-cols-[minmax(0,1.8fr)_120px_120px] gap-4 border-b border-border/70 px-6 py-4 text-[11px] font-black uppercase tracking-[0.18em] text-muted-foreground/70">
          <span>Varseltype</span>
          <span>E-post</span>
          <span>SMS</span>
        </div>

        {loading ? (
          <div className="flex items-center justify-center gap-3 px-6 py-12 text-sm font-semibold text-muted-foreground">
            <LoaderCircle className="h-4 w-4 animate-spin" />
            Laster varselinnstillinger...
          </div>
        ) : preferences.length === 0 ? (
          <div className="flex items-center justify-center gap-3 px-6 py-12 text-sm font-semibold text-muted-foreground">
            <Bell className="h-4 w-4" />
            Ingen brukerstyrte varsler er tilgjengelige ennå.
          </div>
        ) : (
          <div>
            {preferences.map((preference) => (
              <div
                key={preference.eventId}
                className="grid grid-cols-[minmax(0,1.8fr)_120px_120px] gap-4 border-b border-border/60 px-6 py-5 last:border-b-0"
              >
                <div className="pr-4">
                  <p className="text-sm font-black text-[#1a2e35]">{preference.name}</p>
                  <p className="mt-1 text-sm leading-relaxed text-muted-foreground">
                    {preference.description}
                  </p>
                </div>

                <div className="flex items-center">
                  {preference.channels.email.available ? (
                    <label className="flex items-center gap-3 text-sm font-semibold text-[#1a2e35]">
                      <ChannelSwitch
                        checked={preference.channels.email.enabled}
                        disabled={saving}
                        onChange={() => handleToggle(preference.eventId, "email")}
                        label={`${preference.name} e-post`}
                      />
                      <span>Aktiv</span>
                    </label>
                  ) : null}
                </div>

                <div className="flex items-center">
                  {preference.channels.sms.available ? (
                    <label className="flex items-center gap-3 text-sm font-semibold text-[#1a2e35]">
                      <ChannelSwitch
                        checked={preference.channels.sms.enabled}
                        disabled={saving}
                        onChange={() => handleToggle(preference.eventId, "sms")}
                        label={`${preference.name} sms`}
                      />
                      <span>Aktiv</span>
                    </label>
                  ) : null}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="mt-6 flex items-center justify-between gap-4">
        <p className="text-sm font-medium text-muted-foreground">
          Endringer gjelder bare for varsler som sendes til din bruker.
        </p>

        <Button
          type="button"
          disabled={saving || loading || !hasUnsavedChanges}
          onClick={() => void handleSave()}
          className="rounded-xl px-6 py-5 text-sm font-bold"
        >
          {saving ? <LoaderCircle className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
          Lagre innstillinger
        </Button>
      </div>
    </section>
  );
}
